import { Link, usePage } from "@inertiajs/react";
import {
  ArrowLeftRight,
  Bell,
  EvCharger,
  Gift,
  HelpCircle,
  LayoutDashboard,
  PlusCircle,
  Settings,
  Tags,
  Users,
} from "lucide-react";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import elTigreLogo from "@/assets/images/brand/primary-white-horizontal.svg";
import elTigreLogoVertical from "@/assets/images/brand/primary-white-vertical.svg";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { CreateTransactionSheet } from "@/components/transactions/create-transaction-sheet/create-transaction-sheet";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  CreateTransactionSheetProvider,
  useCreateTransactionSheet,
} from "@/contexts/create-transaction-sheet-context";
import { QueryProvider } from "@/integrations/tanstack-query/query-provider";
import { cn } from "@/lib/utils";

interface Props {
  breadcrumbs: Array<{ name: string; path?: string }>;
  loyalty_plan_path: string;
  [key: string]: unknown;
}

function SidebarAutoCollapse() {
  const { setOpen } = useSidebar();
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1281px)");
    setOpenRef.current(mql.matches);

    function handleChange(e: MediaQueryListEvent) {
      setOpenRef.current(e.matches);
    }

    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  return null;
}

function BackofficeLayoutContent({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const { url, props } = usePage<Props>();
  const { breadcrumbs = [], loyalty_plan_path } = props;
  const { isOpen, openSheet, closeSheet } = useCreateTransactionSheet();

  const isActive = (path: string) => {
    // For root path, use exact match
    if (path === "/") {
      return url === "/";
    }

    // For other paths, use startsWith
    return url.startsWith(path);
  };

  const sidebarItems = {
    versions: ["1.0.1"],
    navMain: [
      {
        items: [
          { title: t("sidebar.dashboard"), url: "/", icon: LayoutDashboard },
          {
            title: t("sidebar.stations"),
            url: "/stations",
            icon: EvCharger,
          },
        ],
      },
      {
        title: t("sidebar.loyalty_program"),
        url: "#",
        items: [
          {
            title: t("sidebar.program"),
            url: loyalty_plan_path || "/loyalty_plans",
            isActive: url.startsWith("/loyalty_plans"),
            icon: LayoutDashboard,
          },
          {
            title: t("sidebar.reward_categories"),
            url: "/reward_categories",
            icon: Tags,
          },
          {
            title: t("sidebar.rewards"),
            url: "/rewards",
            icon: Gift,
          },
          {
            title: t("sidebar.transactions"),
            url: "/point_transactions",
            icon: ArrowLeftRight,
          },
          {
            title: t("sidebar.faq"),
            url: "/faqs",
            icon: HelpCircle,
          },
        ],
      },
      {
        title: t("sidebar.users_and_accounts"),
        url: "#",
        items: [
          {
            title: t("sidebar.users"),
            url: "/users",
            icon: Users,
          },
        ],
      },
      {
        title: t("sidebar.notifications"),
        url: "#",
        items: [
          {
            title: t("sidebar.campaigns"),
            url: "/campaigns",
            icon: Bell,
          },
        ],
      },
      {
        title: t("sidebar.settings_group"),
        url: "#",
        items: [
          {
            title: t("sidebar.settings"),
            url: "/settings",
            icon: Settings,
          },
        ],
      },
    ],
  };

  return (
    <QueryProvider>
      <Toaster richColors />
      <TooltipProvider delayDuration={0}>
        <SidebarProvider defaultOpen={false}>
          <SidebarAutoCollapse />
          <Sidebar
            collapsible="icon"
            className="bg-linear-to-b from-secondary-500 to-secondary-800 border-none"
          >
            <SidebarHeader className="pt-4">
              <Link
                href="/"
                className="flex-none rounded-xl text-xl inline-block font-semibold focus:outline-none focus:opacity-80"
              >
                <img
                  src={elTigreLogo}
                  className="w-full mx-auto h-48 object-cover group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden"
                  alt="El Tigre Logo"
                />
                <img
                  src={elTigreLogoVertical}
                  className="hidden h-10 w-auto mx-auto group-data-[collapsible=icon]:group-data-[state=collapsed]:block"
                  alt="El Tigre Logo"
                />
              </Link>
            </SidebarHeader>

            <SidebarContent>
              {/* We create a SidebarGroup for each parent. */}
              {sidebarItems.navMain.map((item, index) => (
                <SidebarGroup key={item.title || `group-${index}`}>
                  {item.title && (
                    <SidebarGroupLabel className="text-white/70 uppercase text-xs">
                      {item.title}
                    </SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {item.items.map((item) => {
                        const active = "isActive" in item ? item.isActive : isActive(item.url);

                        return (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton
                              asChild
                              size="lg"
                              isActive={active}
                              tooltip={item.title}
                              className={cn(
                                "text-white font-semibold hover:bg-white/20 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white active:bg-white/30 active:text-white",
                                active && "bg-black/20",
                              )}
                            >
                              <Link href={item.url}>
                                <item.icon />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </SidebarContent>
          </Sidebar>

          <SidebarInset>
            <header className="sticky top-0 flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-white px-4 z-50">
              <SidebarTrigger />
              <Button variant="ghost" size="sm" onClick={openSheet}>
                <PlusCircle className="mr-2 h-4 w-4" />
                {t("common.new_transaction")}
              </Button>
            </header>

            <CreateTransactionSheet
              open={isOpen}
              onOpenChange={(open) => (open ? openSheet() : closeSheet())}
            />

            <main className="flex-1 bg-neutral-100 p-4 md:p-8">
              {breadcrumbs?.length > 0 ? (
                <div>
                  <Breadcrumbs items={breadcrumbs} />
                </div>
              ) : null}

              {children}
            </main>
          </SidebarInset>
        </SidebarProvider>
      </TooltipProvider>
    </QueryProvider>
  );
}

export default function BackofficeLayout({ children }: PropsWithChildren) {
  return (
    <CreateTransactionSheetProvider>
      <BackofficeLayoutContent>{children}</BackofficeLayoutContent>
    </CreateTransactionSheetProvider>
  );
}
